import { keepPreviousData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { growthKeys } from '@/data/queryKeys';
import { useNotify } from '@/data/notifications';
import {
  fetchKeywordBrief,
  fetchKeywords,
  KEYWORDS_PAGE_SIZE,
  regenerateStrategyBrief,
} from '../api';

export function useKeywords(filters?: { category?: string; page?: number; limit?: number }) {
  const page = filters?.page ?? 1;
  const limit = filters?.limit ?? KEYWORDS_PAGE_SIZE;
  const category = filters?.category;

  return useGrowthQuery({
    queryKey: growthKeys.keywords.list({ category, page, limit }),
    queryFn: () =>
      fetchKeywords({
        page,
        limit,
        ...(category && category !== 'All' ? { category } : {}),
      }),
    placeholderData: keepPreviousData,
  });
}

export function useKeywordBrief(id: string, enabled = true) {
  return useGrowthQuery({
    queryKey: growthKeys.keywords.brief(id),
    queryFn: () => fetchKeywordBrief(id),
    enabled: enabled && Boolean(id),
    refetchInterval: (query) => (query.state.data?.generating ? 3000 : false),
  });
}

export function useKeywordMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const regenerate = useMutation({
    mutationFn: regenerateStrategyBrief,
    onSuccess: (_data, id) => {
      notify.success('Strategy brief regeneration queued');
      void queryClient.invalidateQueries({ queryKey: growthKeys.keywords.brief(id) });
    },
    onError: (e) => notify.error(e),
  });

  return { regenerate };
}
